import { Controller } from "@hotwired/stimulus"

import { STYLE, loadMapLibre } from "../lib/maplibre"

// The club map behind a "Where to spend" section: every placed partner pinned
// on the shared basemap (lib/maplibre — the same tiles as a place's own map),
// framed to fit them all.
export default class extends Controller {
  static targets = ["canvas"]
  // pinClass because the two products that render this map do not share a
  // palette: the club's pins carry the brunch gradient, a creator's page carries
  // WannaCollab's ink. Everything else here — the tiles, the framing, the
  // marker/pin split — is the same map, so it is one controller with a class
  // name passed in rather than two copies of a hundred subtle lines.
  // cluster (28 Oct 2026, a creator's page of 281 places): the pins near
  // one another fold into one bubble with their count, MapLibre's own
  // clustering deciding which; zooming in opens the bubbles, a tap on one
  // zooms to it. The club map keeps every pin.
  static values = { points: Array, pinClass: { type: String, default: "wb-pin" }, bottomPad: { type: Number, default: 160 },
                    cluster: { type: Boolean, default: false } }

  async connect() {
    // No coordinates, no map: the section still renders, the toggle still
    // works, and the list carries the partners.
    if (this.pointsValue.length === 0) return

    // Every pin, so tapping one can clear the mark off the last. MapLibre hands
    // back no handle on a marker's element, and nothing here ever needs to find
    // a pin by id — an array, not a Map. `Map` in this scope is MapLibre's
    // anyway, destructured below and dead until then.
    this.pins = []

    const { Map, Marker, LngLatBounds, NavigationControl } = await loadMapLibre()
    // Stimulus can disconnect us while that import is in flight — a Turbo
    // navigation away from the page, say. Building a map into a detached
    // element would leak it: disconnect() has already run and will not run again.
    if (!this.element.isConnected) return

    this.map = new Map({
      container: this.canvasTarget,
      style: STYLE,
      // Superseded by fit() below; this is only what shows for the frame
      // before the tiles land. Miami, because that is where the club is.
      center: [-80.19, 25.78],
      zoom: 10,
      attributionControl: { compact: true },
      // The section scrolls inside a phone-width column on two of the three
      // screens that carry it. Grabbing the page scroll to zoom a map the
      // visitor was only scrolling past is the classic embedded-map sin.
      scrollZoom: false,
    })
    this.map.addControl(new NavigationControl({ showCompass: false }), "top-right")
    // Compact means the "i", but MapLibre opens it on load and folds it only
    // at the first drag: on a phone the credit then covers the map's foot and
    // the pins under it. Folded from the start; one tap on the "i" shows it.
    this.map.once("load", () => {
      this.canvasTarget.querySelectorAll(".maplibregl-compact-show")
        .forEach((el) => el.classList.remove("maplibregl-compact-show"))
    })
    this.markers = {}
    this.pointsValue.forEach((point) => this.addPin(point, Marker))
    if (this.clusterValue) this.startClusters(Marker)
    // Straight away, not on `load`: framing only moves the camera, and none of
    // it needs a tile. Hanging it on `load` leaves the map on its placeholder
    // centre for as long as the network takes — and on a slow connection, a
    // map centred on the wrong place is exactly what the visitor sits looking at.
    // Framed only on screen: on the creator's page the map is a tab behind
    // the list (13 Oct 2026), display:none, and fitBounds on a 0×0 box
    // frames nothing. resize() frames it the first time the tab shows.
    this.LngLatBounds = LngLatBounds
    if (this.canvasTarget.clientWidth > 0) this.fit()
  }

  disconnect() {
    this.map?.remove()
    this.map = null
    this.pins = null
    this.markers = null
  }

  // Called by the view controller when the map comes back from behind the
  // list, where it was `display:none` and measured 0×0.
  resize() {
    if (!this.map) return
    this.map.resize()
    if (!this.fitted) this.fit()
  }

  // The filters above a creator's map (place_filters_controller) send the
  // places still listed: the other pins go, and the frame closes on what is
  // left. Kept for the pins too when it lands before MapLibre has loaded.
  filter(event) {
    this.shown = new Set(event.detail.ids)
    if (!this.map) return
    if (this.clusterValue) {
      this.map.getSource("pins")?.setData(this.features())
      if (this.canvasTarget.clientWidth > 0) this.fit()
      return
    }
    this.pins.forEach((pin) => {
      pin.parentElement.style.display = this.shown.has(pin.dataset.id) ? "" : "none"
    })
    if (this.canvasTarget.clientWidth > 0) this.fit()
  }

  // A tap on a pin scrolls the card strip to that partner — the section wires
  // `select` to the view controller, which owns the strip. The pin itself only
  // has to say which one is being shown.
  addPin(point, Marker) {
    const pin = document.createElement("button")
    pin.type = "button"
    pin.className = this.pinClassValue
    // The label is the pin's whole content, and it is optional. On the club map
    // it is the cashback rate: rates are per-partner now, so a field of
    // identical dots would say nothing about which place is worth crossing town
    // for. A creator's page has no such number — every pin there is the same
    // kind of thing — so it sends none and the pins stay dots.
    if (point.label) pin.textContent = point.label
    if (point.photo) {
      pin.classList.add("has-photo")
      pin.style.backgroundImage = `url("${point.photo.replace(/"/g, "%22")}")`
    }
    pin.dataset.id = point.id
    pin.setAttribute("aria-label", [point.name, point.label].filter(Boolean).join(", "))
    pin.addEventListener("click", () => {
      this.pins.forEach((other) => delete other.dataset.active)
      pin.dataset.active = ""
      this.dispatch("select", { detail: { id: point.id } })
    })
    this.pins.push(pin)

    // The marker's element is a wrapper, never the pin. MapLibre holds a marker
    // in place by writing `transform: translate(-50%,-50%) translate(x,y)` onto
    // whatever element it was handed, and .wb-pin's `hover:scale-150` compiles
    // to the individual `scale` property. Those do not override one another —
    // they COMPOSE, in the order translate → rotate → scale → transform, so the
    // scale multiplies MapLibre's translation as well as the pin. Hovering sent
    // the pin flying away from the map's origin, 1.5× as far out as it belonged.
    // On two elements there is nothing left to compose.
    const anchor = document.createElement("div")
    anchor.appendChild(pin)
    if (this.shown && !this.shown.has(point.id)) anchor.style.display = "none"

    const marker = new Marker({ element: anchor }).setLngLat([point.lng, point.lat])
    this.markers[String(point.id)] = marker
    // Clustered, a pin is put on the map only when no bubble holds it.
    if (!this.clusterValue) marker.addTo(this.map)
  }

  // The places still shown, as the GeoJSON MapLibre clusters.
  features() {
    const points = this.pointsValue.filter((p) => !this.shown || this.shown.has(p.id))
    return {
      type: "FeatureCollection",
      features: points.map((p) => ({ type: "Feature", geometry: { type: "Point", coordinates: [p.lng, p.lat] },
                                     properties: { id: String(p.id) } })),
    }
  }

  // The source needs the style, hence `load`. An invisible layer reads it, or
  // MapLibre would never load the source's tiles for querySourceFeatures.
  startClusters(Marker) {
    this.Marker = Marker
    this.bubbles = {}
    this.onScreen = {}
    this.map.on("load", () => {
      this.map.addSource("pins", { type: "geojson", data: this.features(), cluster: true, clusterRadius: 48, clusterMaxZoom: 15 })
      this.map.addLayer({ id: "pins-read", type: "circle", source: "pins", paint: { "circle-radius": 0, "circle-opacity": 0 } })
      this.map.on("render", () => this.syncClusters())
    })
  }

  // What the map shows now: a bubble per cluster, the photo pin of every
  // place left alone — added when it appears, removed when it goes.
  syncClusters() {
    if (!this.map?.getSource("pins") || !this.map.isSourceLoaded("pins")) return
    const next = {}
    this.map.querySourceFeatures("pins").forEach((f) => {
      const props = f.properties
      if (props.cluster) {
        const key = `c${props.cluster_id}`
        next[key] ||= this.bubbles[key] || (this.bubbles[key] = this.bubble(props, f.geometry.coordinates))
      } else if (this.markers[props.id]) {
        next[props.id] ||= this.markers[props.id]
      }
    })
    Object.entries(next).forEach(([key, marker]) => { if (!this.onScreen[key]) marker.addTo(this.map) })
    Object.entries(this.onScreen).forEach(([key, marker]) => {
      if (next[key]) return
      marker.remove()
      delete this.bubbles[key]
    })
    this.onScreen = next
  }

  bubble(props, coordinates) {
    const el = document.createElement("button")
    el.type = "button"
    el.className = "wc-cluster"
    const count = props.point_count
    el.textContent = props.point_count_abbreviated
    el.style.setProperty("--size", `${count < 10 ? 34 : count < 50 ? 42 : 52}px`)
    el.setAttribute("aria-label", `${count} places, zoom in`)
    el.addEventListener("click", async () => {
      const zoom = await this.map.getSource("pins").getClusterExpansionZoom(props.cluster_id)
      this.map.easeTo({ center: coordinates, zoom })
    })
    // Wrapped like a pin (see addPin): the hover's scale must not compose
    // with the transform MapLibre places the marker by.
    const anchor = document.createElement("div")
    anchor.appendChild(el)
    return new this.Marker({ element: anchor }).setLngLat(coordinates)
  }

  // The club is spread over the width of Miami — Coconut Grove to Hallandale
  // Beach is some 40km — so the frame is derived from the pins rather than
  // hard-coded. maxZoom because a single partner is a point, not a box, and
  // fitBounds on it would otherwise go to maximum zoom and show one rooftop.
  //
  // The bottom padding clears the card strip. Even padding put pins underneath
  // it, where they are not merely hidden but unclickable — the strip is on top
  // and eats the pointer. The frame has to treat that band as off the map.
  fit() {
    this.fitted = true
    const points = this.pointsValue.filter((p) => !this.shown || this.shown.has(p.id))
    if (points.length === 0) return
    const bounds = new this.LngLatBounds()
    points.forEach((p) => bounds.extend([p.lng, p.lat]))
    this.map.fitBounds(bounds, {
      padding: { top: 48, left: 48, right: 48, bottom: this.bottomPadValue },
      maxZoom: 14,
      duration: 0,
    })
  }
}
